import { FormattedMessage } from 'react-intl';
import { RouteObject } from 'react-router-dom';
import { redirect } from 'react-router-dom';

import { DeveloperPortalCommands } from '@tdp/core';
import { LazyRegionDetails, AnalyticsEntry, LazyAppOverview } from '@tdp/modules/apps';

import { createRoutes } from './navigationContext';
import { AppFeatureRoutes } from './pages/Features/AppFeatureRoutes';

export interface AppDetailRoutesDeps {
  commands: DeveloperPortalCommands;
}
export function createAppDetailRoutes(): RouteObject[] {
  //TODO(nibeauli): we will need to detect 1P users and add/remove items accordingly
  //TODO(nibeauli): we will need the ability to add/remove items based on ecs flags as well
  const childRoutes = createRoutes({
    layout: async () => {
      const { AppDetailsLayout } = await import('./layout');
      return <AppDetailsLayout />;
    },
    sections: [
      {
        id: 'overview',
        routes: [
          {
            path: 'dashboard',
            data: {
              title: <FormattedMessage defaultMessage="Dashboard" />,
            },
            page: async () => {
              const { DashboardPage } = await import('./pages/Dashboard');
              return <DashboardPage />;
            },
          },
          /*{
            path: 'overview',
            data: {
              title: <FormattedMessage defaultMessage="Overview" />,
            },
            routes: [
              {
                path: '',
                element: <LazyAppOverview />
              }
            ]
          },*/
          {
            path: 'analytics',
            data: {
              title: <FormattedMessage defaultMessage="Analytics" />,
            },
            routes: [
              {
                path: 'region',
                element: <LazyRegionDetails />

              },
              {
                path: '',
                element: <AnalyticsEntry />
              }
            ]
          }
        ],
        data: {
          title: <FormattedMessage defaultMessage="Overview" />
        }
      },
      {
        id: 'configure',
        data: {
          title: <FormattedMessage defaultMessage="Configure" />
        },
        routes: [
          {
            path: 'details',
            data: {
              title: <FormattedMessage defaultMessage="Basic information" />
            },
            page: async () => {
              const { BasicInformationPage } = await import('./pages/BasicInformation');
              return <BasicInformationPage />;
            }
          },
          {
            path: 'branding',
            data: {
              title: <FormattedMessage defaultMessage="Branding" description="Button on left navigation for navigating to the branding page" />
            },
            page: async () => {
              const { BrandingPage } = await import('./pages/Branding');
              return <BrandingPage />;
            }
          },
          {
            path: 'app-features',
            data: {
              title: <FormattedMessage defaultMessage="App features" />
            },
            page: async () => {
              const { AppFeaturesPage } = await import('./pages/Features/AppFeaturesPage');
              return <AppFeaturesPage />;
            },
            routes: AppFeatureRoutes()
          },
          {
            path: 'app-package-editor',
            data: {
              title: <FormattedMessage defaultMessage="App package editor" />
            },
            page: async () => {
              const { PackageEditorPage } = await import('@tdp/modules/apps/packageEditor');
              return <PackageEditorPage />;
            }
          },
          {
            path: 'permissions',
            data: {
              title: <FormattedMessage defaultMessage="Permissions" />
            },
            page: async () => {
              const { AppPermissionsPage } = await import('./pages/Permissions/AppPermissionsPage');
              return <AppPermissionsPage />;
            }
          },
          {
            path: 'single-sign-on',
            data: {
              title: <FormattedMessage defaultMessage="Single sign-on" />
            },
            page: async () => {
              const { SingleSignOn } = await import('./pages/SingleSignOn/SingleSignOn');
              return <SingleSignOn />;
            }
          },
          {
            path: 'languages',
            data: {
              title: <FormattedMessage defaultMessage="Languages" />
            },
            page: async () => {
              const { LanguagesPage } = await import('./pages/Languages/LanguagesPage');
              return <LanguagesPage />;
            }
          },
          {
            path: 'domains',
            data: {
              title: <FormattedMessage defaultMessage="Domains" />
            },
            page: async () => {
              const { AppDetailsDomain } = await import('./pages/Domain/AppDetailsDomain');
              return <AppDetailsDomain />;
            }
          }
        ]
      },
      {
        id: 'advanced',
        data: {
          title: <FormattedMessage defaultMessage="Advanced" />
        },
        routes: [
          {
            path: 'owners',
            data: {
              title: <FormattedMessage defaultMessage="Owners" />
            },
            page: async () => {
              const { AppOwners } = await import('./pages/Owners/AppOwners');
              return <AppOwners />;
            }
          },
          {
            path: 'app-content',
            data: {
              title: <FormattedMessage defaultMessage="App content" />
            },
            page: async () => {
              const { AppContent } = await import('./pages/AppContent/AppContent');
              return <AppContent />;
            }
          },
          /**
         * @remarks
         * - The `plans-pricing` route under the "Advanced" section is currently commented out due to issues with invalid fields in the Plans and Pricing component.
         *   Please review and fix the invalid fields in the PlansPricing page before enabling this route.
         */
          // {
          //   path: 'plans-pricing',
          //   data: {
          //     title: <FormattedMessage defaultMessage="Plans and pricing" />
          //   },
          //   page: async () => {
          //     const { PlansPricing } = await import('./pages/Monet/PlansPricing');
          //     return <PlansPricing />;
          //   }
          // },
          {
            path: 'admin-settings',
            data: {
              title: <FormattedMessage defaultMessage="Admin settings" />
            },
            page: async () => {
              const { AppAdminSettings } = await import('./pages/AdminSettings/AdminSettings');
              return <AppAdminSettings />;
            }
          },
          {
            path: 'first-party',
            data: {
              title: <FormattedMessage defaultMessage="First party settings" />
            },
            page: async () => {
              const { FirstPartyPage } = await import('./pages/FirstPartySettings/FirstPartyPage');
              return <FirstPartyPage />;
            }
          }
        ]
      },
      {
        id: 'develop',
        data: {
          title: <FormattedMessage defaultMessage="Develop" />
        },
        routes: [
          {
            path: 'develop-with-teams-toolkit',
            data: {
              title: <FormattedMessage defaultMessage="Open in Microsoft 365 Agents Toolkit" />
            },
            page: async () => {
              const { DevelopToolkitPage } = await import('./pages/Toolkit/DevelopToolkitPage');
              return <DevelopToolkitPage />;
            }
          }
        ],
      },
      {
        id: 'publish',
        data: {
          title: <FormattedMessage defaultMessage="Publish" />
        },
        routes: [
          {
            path: 'validation',
            data: {
              title: <FormattedMessage defaultMessage="App validation" />
            },
            // TODO(nibeauli): factor this into a validation routing file
            routes: [
              {
                path: ':validationId',
                lazy: async () => {
                  const { AppValidationDetailPage } = await import('@tdp/modules/apps');
                  return {
                    element: <AppValidationDetailPage />
                  }
                }
              },
              {
                path: '', // strange root level trickery, but it works. (note * only matches validation/{something} not validation bare)
                lazy: async () => {
                  const { AppValidationPage } = await import('@tdp/modules/apps');
                  return {
                    element: <AppValidationPage />
                  }
                }
              }
            ]
          },
          {
            path: 'publish-org',
            data: {
              title: <FormattedMessage defaultMessage="Publish to org" />
            },
            page: async () => {
              const { PublishOrgPage } = await import('./pages/Publish/PublishOrgPage');
              return <PublishOrgPage />;
            }
          },
          {
            path: 'publish-store',
            data: {
              title: <FormattedMessage defaultMessage="Publish to store" />
            },
            page: async () => {
              const { PublishStorePage } = await import('./pages/Publish/PublishStorePage');
              return <PublishStorePage />;
            }
          }
        ]
      },
      {
        id: 'flights',
        data: {
          title: <FormattedMessage defaultMessage="Manage rollouts" />
        },
        routes: [
          {
            path: 'history',
            data: {
              title: <FormattedMessage defaultMessage="Pull request history" />
            },
            page: async () => {
              const { PullRequestHistoryPage } = await import('./pages/AppFlight/PullRequestHistoryPage');
              return <PullRequestHistoryPage />;
            }
          },
          {
            path: 'new-flights',
            data: {
              title: <FormattedMessage defaultMessage="Distribute your app" />
            },
            page: async () => {
              const { CreateNewFlightPage } = await import('./pages/AppFlight/CreateNewFlightPage');
              return <CreateNewFlightPage />;
            }
          },
          {
            path: 'withdraw',
            data: {
              title: <FormattedMessage defaultMessage="Withdraw your app" />
            },
            page: async () => {
              const { AppWithdrawPage } = await import('./pages/AppFlight/AppWithdrawPage');
              return <AppWithdrawPage />;
            }
          },
          {
            path: 'app-contact-info',
            data: {
              title: <FormattedMessage defaultMessage="App contact information" />
            },
            page: async () => {
              const { AppContactInformationPage } = await import('./pages/AppFlight/AppContactInformationPage');
              return <AppContactInformationPage />;
            }
          }
        ],
      },
      {
        // NOTE: This section MUST be placed at the end of the sections array
        // The wildcard route ('*') acts as a catch-all for any undefined routes
        // If placed earlier, it would intercept valid routes that come after it
        // ensuring proper route resolution order in React Router
        id: 'error-pages',
        data: {
          title: <FormattedMessage defaultMessage="Error Pages" />
        },
        routes: [
          {
            path: '*',
            data: {
              title: <FormattedMessage defaultMessage="Page Not Found" />
            },
            page: async () => {
              const { PageNotFound } = await import('@tdp/core/');
              return <PageNotFound />;
            }
          }
        ],
      },
    ]
  });

  //TODO(nibeauli): add a redirect / 404 route here
  return [
    {
      path: ':appId',
      children: [
        ...childRoutes,
        // Add redirect route to dashboard as default
        {
          path: '',
          loader: () => {
            return redirect('dashboard');
          }
        }
      ],
    },
  ];
}
